import React, {useCallback, useEffect, useState} from "react";
import {Alert, Button, Card, Empty, Input, Skeleton, Typography} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Link} from "react-router-dom";
import {useTranslate} from "../../../../hooks/translate.hook";
import {reportsService} from "../../../../services/reports/reports.service";
import {ReportCategory} from "../../../../models/reports/reports";

export const ReportsMainPageCategories: React.FC = () => {
    const t = useTranslate();
    const [categories, setCategories] = useState<Record<string, ReportCategory[]>>({});
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);

    const loadCategories = useCallback(async () => {
        setLoading(true);
        setLoadFailed(false);

        try {
            setCategories(await reportsService.getCategories());
        } catch {
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadCategories();
    }, [loadCategories]);

    const normalizedSearch = search.trim().toLocaleLowerCase();
    const visibleCategories = Object.entries(categories)
        .map(([categoryName, reports]) => {
            const categoryTitle = t("REPORTS." + categoryName.toUpperCase() + ".TITLE");
            const categoryMatches = categoryTitle.toLocaleLowerCase().includes(normalizedSearch);
            const visibleReports = normalizedSearch && !categoryMatches
                ? reports.filter((report) => {
                    const reportTitle = t("REPORTS." + report.name.toUpperCase() + ".TITLE");
                    return reportTitle.toLocaleLowerCase().includes(normalizedSearch)
                        || report.name.toLocaleLowerCase().includes(normalizedSearch);
                })
                : reports;

            return {categoryName, categoryTitle, reports: visibleReports};
        })
        .filter((category) => category.reports.length > 0);

    return (
        <section className="reports-catalog">
            <div className="reports-catalog__header">
                <Typography.Text type="secondary">
                    {t("REPORTS.CATALOG.DESCRIPTION", {
                        defaultValue: "Choose a report to view, filter or export.",
                    })}
                </Typography.Text>
                <Input.Search
                    className="reports-catalog__search"
                    allowClear
                    disabled={loading || loadFailed}
                    placeholder={t("REPORTS.CATALOG.SEARCH", {defaultValue: "Search reports"})}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                />
            </div>

            {loading ? (
                <div className="reports-catalog__grid">
                    {[0, 1, 2, 3].map((item) => (
                        <Card key={item} className="reports-catalog__skeleton-card">
                            <Skeleton active title={{width: "45%"}} paragraph={{rows: 3}} />
                        </Card>
                    ))}
                </div>
            ) : loadFailed ? (
                <Alert
                    showIcon
                    type="error"
                    message={t("REPORTS.CATALOG.LOAD_ERROR", {
                        defaultValue: "Reports could not be loaded.",
                    })}
                    action={(
                        <Button size="small" onClick={() => void loadCategories()}>
                            {t("RETRY", {defaultValue: "Retry"})}
                        </Button>
                    )}
                />
            ) : visibleCategories.length === 0 ? (
                <Empty
                    description={search
                        ? t("REPORTS.CATALOG.NO_RESULTS", {defaultValue: "No matching reports"})
                        : t("REPORTS.CATALOG.EMPTY", {defaultValue: "No reports available"})}
                />
            ) : (
                <div className="reports-catalog__grid">
                    {visibleCategories.map(({categoryName, categoryTitle, reports}) => (
                        <Card
                            key={categoryName}
                            className="reports-catalog__category"
                            title={(
                                <div className="reports-catalog__category-heading">
                                    <span>{categoryTitle}</span>
                                    <span
                                        className="reports-catalog__category-count"
                                        aria-label={t("REPORTS.CATALOG.COUNT", {
                                            count: reports.length,
                                            defaultValue: "{{count}} reports",
                                        })}
                                    >
                                        {reports.length}
                                    </span>
                                </div>
                            )}
                        >
                            <div className="reports-catalog__links">
                                {reports.map((report) => (
                                    <Link
                                        key={report.name}
                                        className="reports-catalog__link"
                                        to={"/reports/" + report.name}
                                    >
                                        <span className="reports-catalog__report-icon" aria-hidden="true">
                                            <FontAwesomeIcon icon={["far", "file"]} />
                                        </span>
                                        <span className="reports-catalog__report-title">
                                            {t("REPORTS." + report.name.toUpperCase() + ".TITLE")}
                                        </span>
                                        <FontAwesomeIcon
                                            className="reports-catalog__link-arrow"
                                            icon={["fas", "chevron-right"]}
                                            aria-hidden="true"
                                        />
                                    </Link>
                                ))}
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </section>
    );
};
